import { Typography, useTheme } from "@mui/material"
import Box from "@mui/material/Box"

import type { SxProps } from "@mui/material"

export interface AngleViewLayoutProps {
  src: string
  viewKey: "first" | "second"
  displayName: string
  imgBoxStyles: SxProps
  imgStyles: SxProps
  valueBoxStyles: SxProps
  alt?: string
}

/**
 * Shared, installation-agnostic shell for the angle view: the centered divider,
 * the value label and the rotated image. Variant-specific positioning is
 * computed by `PowerlineAngleView` / `TowerAngleView` and passed in as styles.
 */
export const AngleViewLayout = ({
  src,
  viewKey,
  displayName,
  imgBoxStyles,
  imgStyles,
  valueBoxStyles,
  alt,
}: AngleViewLayoutProps) => {
  const theme = useTheme()
  const paletteKey = viewKey === "first" ? "alpha" : "beta"

  return (
    <Box sx={{ position: "relative", height: "100%" }}>
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          top: "5%",
          height: "90%",
          borderLeft: "1px solid",
          borderColor: "neutral.300",
          zIndex: 0,
        }}
      />
      <Box sx={valueBoxStyles}>
        <Typography
          variant="h5"
          sx={{
            whiteSpace: "nowrap",
            color: theme.palette[paletteKey]?.main ?? theme.palette.text.primary,
            ml: "auto",
            mr: "auto",
            width: "fit-content",
            p: "0.3rem 0.7rem",
            backgroundColor: theme.palette.background.paper,
            zIndex: 10,
          }}
        >
          {displayName}
        </Typography>
      </Box>
      <Box
        justifyContent="center"
        alignItems="center"
        width="100%"
        overflow="hidden"
        display="flex"
        sx={imgBoxStyles}
      >
        <Box
          component="img"
          src={src}
          alt={alt ?? `${displayName} angle view`}
          sx={imgStyles}
        />
      </Box>
    </Box>
  )
}
